import { create } from 'zustand';
import { useAuthStore } from './auth-store';
import { sessionScope } from '@/features/auth/session-scope';
import { readableText, isValidHex } from '@/lib/utils/contrast';

export type ThemeMode = 'light' | 'dark' | 'system';
export type TimeFormat = '12h' | '24h';

export interface ThemePrefs {
  mode: ThemeMode;
  accent: string | null;     // null => token default
  bubbleOut: string | null;  // null => token default
  timeFormat: TimeFormat;    // Personal clock format; 12h is the product default.
}

// Default mode is light (product decision); users opt into system/dark explicitly.
// timeFormat defaults to '12h' (AM/PM) — product decision (owner request); users
// who prefer 24h opt in via Apariencia.
export const THEME_DEFAULTS: ThemePrefs = {
  mode: 'light', accent: null, bubbleOut: null, timeFormat: '12h',
};

function cacheKey(){return 'blaze-theme:'+sessionScope(useAuthStore.getState());}
let intentRevision=0,confirmedRevision=0;
export const themeIntentRevision=()=>intentRevision;
export const themeHasUnsavedChanges=()=>intentRevision!==confirmedRevision;

function loadCache(): ThemePrefs {
  try {
    const raw = localStorage.getItem(cacheKey());
    if (!raw) return { ...THEME_DEFAULTS };
    const p = JSON.parse(raw) as Partial<ThemePrefs>;
    return {
      mode: p.mode === 'dark' || p.mode === 'system' ? p.mode : 'light',
      accent: typeof p.accent === 'string' && isValidHex(p.accent) ? p.accent : null,
      bubbleOut: typeof p.bubbleOut === 'string' && isValidHex(p.bubbleOut) ? p.bubbleOut : null,
      timeFormat: p.timeFormat === '24h' ? '24h' : '12h',
    };
  } catch {
    return { ...THEME_DEFAULTS };
  }
}

function saveCache(p: ThemePrefs) {
  try {
    localStorage.setItem(cacheKey(), JSON.stringify({mode:p.mode,accent:p.accent,bubbleOut:p.bubbleOut,timeFormat:p.timeFormat}));
  } catch {
    /* ignore quota/SSR */
  }
}

function resolveMode(mode: ThemeMode): 'light' | 'dark' {
  if (mode !== 'system') return mode;
  return typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

let mql: MediaQueryList | null = null;
let mqlHandler: (() => void) | null = null;

/**
 * Apply prefs to <html>: data-theme + inline color vars (or remove them).
 * Only the *visual* fields matter here — timeFormat doesn't paint the DOM, so
 * the param is narrowed to keep callers (and tests) honest about that.
 */
export function applyTheme(p: Pick<ThemePrefs, 'mode' | 'accent' | 'bubbleOut'>) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('data-theme', resolveMode(p.mode));

  const setVar = (name: string, val: string | null) => {
    if (val && isValidHex(val)) root.style.setProperty(name, val);
    else root.style.removeProperty(name);
  };
  setVar('--accent', p.accent);
  setVar('--accent-fg', p.accent && isValidHex(p.accent) ? readableText(p.accent) : null);
  setVar('--bubble-out', p.bubbleOut);
  setVar('--bubble-out-fg', p.bubbleOut && isValidHex(p.bubbleOut) ? readableText(p.bubbleOut) : null);

  // Keep `data-theme` in sync with OS while in `system` mode; detach otherwise.
  if (mql && mqlHandler) {
    mql.removeEventListener('change', mqlHandler);
    mql = null;
    mqlHandler = null;
  }
  if (p.mode === 'system' && typeof window !== 'undefined' && window.matchMedia) {
    mql = window.matchMedia('(prefers-color-scheme: dark)');
    mqlHandler = () => root.setAttribute('data-theme', resolveMode('system'));
    mql.addEventListener('change', mqlHandler);
  }
}

interface ThemeStore extends ThemePrefs {
  setMode: (m: ThemeMode) => void;
  setAccent: (v: string | null) => void;
  setBubbleOut: (v: string | null) => void;
  setTimeFormat: (v: TimeFormat) => void;
  setAll: (p: ThemePrefs) => void;
  reset: () => void;
}

function commit(set: (partial: Partial<ThemeStore>) => void, next: ThemePrefs) {
  intentRevision++;
  saveCache(next);
  applyTheme(next);
  set(next);
}

export const useThemeStore = create<ThemeStore>((set, get) => {
  const initial = loadCache();
  // Paint immediately on store creation (before React renders).
  applyTheme(initial);
  return {
    ...initial,
    setMode: (m) => commit(set, { ...get(), mode: m }),
    setAccent: (v) => { if (v !== null && !isValidHex(v)) return; commit(set, { ...get(), accent: v }); },
    setBubbleOut: (v) => { if (v !== null && !isValidHex(v)) return; commit(set, { ...get(), bubbleOut: v }); },
    setTimeFormat: (v) => commit(set, { ...get(), timeFormat: v }),
    setAll: (p) => {saveCache(p);applyTheme(p);set(p);},
    reset: () => commit(set, { ...THEME_DEFAULTS }),
  };
});

// Hydration never overwrites a newer local preview awaiting its own write.
export function hydrateTheme(p:ThemePrefs){if(!themeHasUnsavedChanges())useThemeStore.getState().setAll(p);}
export function confirmTheme(p:ThemePrefs,revision:number){if(revision!==intentRevision)return;confirmedRevision=revision;useThemeStore.getState().setAll(p);}
export function adoptStoredTheme(p:ThemePrefs){intentRevision++;confirmedRevision=intentRevision;useThemeStore.getState().setAll(p);}
useAuthStore.subscribe((next,previous)=>{
 if(sessionScope(next)===sessionScope(previous))return;
 intentRevision++;confirmedRevision=intentRevision;
 useThemeStore.getState().setAll(loadCache());
});
